// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyInstallment, TyInstallmentPlan } from "@/types";
import { formatDate, formatMoney } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import EmptyState from "@/UI/components/EmptyState";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyPlanTimelineDetails = { plan: TyInstallmentPlan };

type Props = { onClose: () => void; details?: unknown };

/**
 * One plan's whole schedule, down a line.
 *
 * The installments list deliberately shows a plan's **next** row only — twelve rows of one
 * agreement bury the other three customers who owe something this week. The rest of the schedule is
 * still a real question, just a different one: *how does this plan run*, which is read down a
 * timeline rather than across a list, because the gap between two dates is the thing being checked.
 *
 * Nothing here is editable. A payment lands on the plan and the allocator spreads it across these
 * rows, so a row is a consequence and not a control — see `PaymentFormPopup`.
 */
export default function PlanTimelinePopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { plan } = details as TyPlanTimelineDetails;

    const rows = useFetch<{ installments: TyInstallment[] }>("/sales/installments", {
        query: { plan: plan._id, limit: constants.maxPageSize },
    });

    const installments = rows.data?.installments ?? [];
    const now = new Date();

    return (
        <section className="side-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{plan.title || t.sales.plans}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {rows.loading && <p className="plan-timeline__state">{t.common.loading}</p>}
                {rows.error && <p className="plan-timeline__state plan-timeline__state--error">{rows.error}</p>}
                {!rows.loading && !rows.error && !installments.length && <EmptyState icon="subscription" title={t.sales.emptyInstallments} />}

                <ol className="plan-timeline">
                    {installments.map((row) => {
                        /** Paid, or late, or still ahead — the same three states the rows list uses. */
                        const isPaid = row.status === "paid";
                        const isLate = !isPaid && row.status !== "cancelled" && !!row.dueAt && new Date(row.dueAt) < now;
                        const state = isPaid ? "paid" : isLate ? "late" : "ahead";
                        const left = Math.max((row.amount ?? 0) - (row.paidAmount ?? 0), 0);

                        return (
                            <li key={row._id} className="plan-timeline__row" data-state={state}>
                                <span className="plan-timeline__dot" aria-hidden="true">
                                    <Icon name={isPaid ? "check" : isLate ? "history" : "calendar"} size={12} />
                                </span>

                                <div className="plan-timeline__body">
                                    <div className="plan-timeline__head">
                                        <span className="plan-timeline__number">
                                            {t.sales.installmentOf
                                                .replace("{number}", String(row.number))
                                                .replace("{count}", String(plan.count ?? 0))}
                                        </span>
                                        <strong className="plan-timeline__amount">
                                            <bdi dir="ltr">{formatMoney(row.amount ?? 0)}</bdi>
                                            <span className="plan-timeline__currency">{t.properties.currency}</span>
                                        </strong>
                                    </div>

                                    <div className="plan-timeline__meta">
                                        <span>{row.dueAt ? formatDate(row.dueAt, lang) : t.crm.notSet}</span>
                                        <span className="plan-timeline__state-label">{t.sales.statuses[row.status]}</span>
                                    </div>

                                    {/* Only where it says something a full row does not: a part payment. */}
                                    {!isPaid && row.paidAmount > 0 && (
                                        <p className="plan-timeline__partial">
                                            {t.sales.paid} <bdi dir="ltr">{formatMoney(row.paidAmount)}</bdi> · {t.sales.remaining}{" "}
                                            <bdi dir="ltr">{formatMoney(left)}</bdi>
                                        </p>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </div>
        </section>
    );
}
